/**
 * Conversion Engine reporting services (Stage 4).
 *
 * Report A — Conversion Summary by Event: per-event funnel (vouchers issued /
 * new-member / activated / redeemed, friends linked vs activated, booth
 * attendance, current + redeemed value). Backed by the SECURITY DEFINER RPC
 * `get_conversion_summary_by_event` (internal-role scoped; returns total_count
 * on each row for pagination, matching the other report RPCs).
 */

import { supabase } from "./supabase";

export interface ConversionSummaryRow {
  event_id: string;
  event_title: string | null;
  event_type: string | null;
  event_location_state: string | null;
  event_start_date: string | null;
  promoter_id: string | null;
  vouchers_issued: number;
  new_member_vouchers: number;
  vouchers_activated: number;
  vouchers_redeemed: number;
  friends_linked: number;
  friends_activated: number;
  attendance_count: number;
  current_value_total: number;
  redeemed_value_total: number;
  total_count: number;
}

export interface ConversionSummaryFilter {
  orgIds?: string[];
  state?: string;
  type?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}

export async function getConversionSummaryByEvent(
  f: ConversionSummaryFilter,
): Promise<{ rows: ConversionSummaryRow[]; total: number }> {
  const { data, error } = await supabase.rpc(
    "get_conversion_summary_by_event",
    {
      p_org_ids: f.orgIds && f.orgIds.length ? f.orgIds : null,
      p_state: f.state || null,
      p_type: f.type || null,
      p_date_from: f.dateFrom || null,
      p_date_to: f.dateTo || null,
      p_page: f.page ?? 1,
      p_limit: f.limit ?? 50,
    },
  );
  if (error) {
    throw new Error(error.message);
  }
  const rows = (data ?? []) as ConversionSummaryRow[];
  // BIGINT/NUMERIC may arrive as strings — total is the first row's total_count.
  const total = rows.length ? Number(rows[0].total_count) : 0;
  return { rows, total };
}

// ---------------------------------------------------------------------------
// Report B — Promoter Attendance & Fee Reconciliation (inputs only; no fee column).
// ---------------------------------------------------------------------------
export interface PromoterReconciliationRow {
  promoter_id: string | null;
  promoter_name: string | null;
  event_id: string;
  event_title: string | null;
  event_type: string | null;
  event_location_state: string | null;
  event_start_date: string | null;
  attendance_count: number;
  vouchers_issued: number;
  vouchers_activated: number;
  new_member_vouchers: number;
  friends_brought: number;
  friends_activated: number;
  current_value_total: number;
  total_count: number;
}

export interface PromoterReconciliationFilter {
  orgIds?: string[];
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}

export async function getPromoterFeeReconciliation(
  f: PromoterReconciliationFilter,
): Promise<{ rows: PromoterReconciliationRow[]; total: number }> {
  const { data, error } = await supabase.rpc(
    "get_promoter_fee_reconciliation",
    {
      p_org_ids: f.orgIds && f.orgIds.length ? f.orgIds : null,
      p_date_from: f.dateFrom || null,
      p_date_to: f.dateTo || null,
      p_page: f.page ?? 1,
      p_limit: f.limit ?? 50,
    },
  );
  if (error) {
    throw new Error(error.message);
  }
  const rows = (data ?? []) as PromoterReconciliationRow[];
  const total = rows.length ? Number(rows[0].total_count) : 0;
  return { rows, total };
}

// ---------------------------------------------------------------------------
// Report C — MSR Book of Business (credited by booth rep, event-hierarchy fallback).
// ---------------------------------------------------------------------------
export interface MsrBookRow {
  position_id: string | null;
  position_title: string | null;
  position_name: string | null;
  conversions_linked: number;
  conversions_activated: number;
  new_members: number;
  new_member_value: number;
  total_count: number;
}

export interface MsrBookFilter {
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}

export async function getMsrBookOfBusiness(
  f: MsrBookFilter,
): Promise<{ rows: MsrBookRow[]; total: number }> {
  const { data, error } = await supabase.rpc("get_msr_book_of_business", {
    p_date_from: f.dateFrom || null,
    p_date_to: f.dateTo || null,
    p_page: f.page ?? 1,
    p_limit: f.limit ?? 50,
  });
  if (error) {
    throw new Error(error.message);
  }
  const rows = (data ?? []) as MsrBookRow[];
  const total = rows.length ? Number(rows[0].total_count) : 0;
  return { rows, total };
}
